/* eslint-disable */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useLocation, useNavigate } from 'react-router-dom';
import { Button, Input, Space, Typography, message } from 'antd';
import { CaretDownOutlined, CaretUpOutlined, CloseOutlined, EditOutlined, PlusOutlined, SafetyCertificateOutlined, SearchOutlined } from '@ant-design/icons';
import { fetchLicenseDetail, fetchLicenseMatrix, toMatrixSnapshot } from './license-form/licenseApi';
import { formatCustomAccessLabel } from './license-form/licenseFormUtils';
import './ProductCategoryLicense.css';

const { Title, Text } = Typography;

const EMPTY_MATRIX = { columns: [], rows: [], cells: {}, customCounts: {} };

const LICENSE_BADGE_LABELS = {
  'buy-sell': 'ซื้อ+ขาย',
  'buy-only': 'ซื้อเท่านั้น',
  'sell-only': 'ขายเท่านั้น',
  none: '—',
};

function compareThaiText(left, right) {
  return String(left || '').localeCompare(String(right || ''), 'th', {
    sensitivity: 'base',
    numeric: true,
  });
}

function CategoryLicenseDetailColTitle({ children, sortable = false, sortOrder = null, onSort }) {
  if (!sortable) {
    return (
      <span className='category-license-detail-col-title-inner'>
        <span className='category-license-detail-col-title-text'>{children}</span>
      </span>
    );
  }

  return (
    <button
      type='button'
      className={`category-license-detail-col-title-btn${
        sortOrder ? ' category-license-detail-col-title-btn--active' : ''
      }`}
      onClick={onSort}
    >
      <span className='category-license-detail-col-title-text'>{children}</span>
      <span className='category-license-detail-col-sorter' aria-hidden='true'>
        <CaretUpOutlined
          className={`category-license-detail-col-sorter-up${
            sortOrder === 'asc' ? ' is-active' : ''
          }`}
        />
        <CaretDownOutlined
          className={`category-license-detail-col-sorter-down${
            sortOrder === 'desc' ? ' is-active' : ''
          }`}
        />
      </span>
    </button>
  );
}

function categoryDisplayName(category) {
  if (!category) return '';
  if (category.multiline?.length) {
    return category.multiline.join(' ');
  }
  return category.name || '';
}

export function LicenseAccessBadge({ type, customCount = 0, onClick }) {
  if (customCount > 0) {
    return (
      <span
        className='category-license-badge category-license-badge--custom category-license-badge--clickable'
        role='button'
        tabIndex={0}
        onClick={onClick}
        onKeyDown={(event) => {
          if (!onClick) return;
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            onClick(event);
          }
        }}
      >
        {formatCustomAccessLabel(customCount)}
      </span>
    );
  }

  if (type === 'custom') {
    return <span className='category-license-badge category-license-badge--custom'>กำหนดเอง</span>;
  }

  return (
    <span className={`category-license-badge category-license-badge--${type || 'none'}`}>
      {LICENSE_BADGE_LABELS[type] || LICENSE_BADGE_LABELS.none}
    </span>
  );
}

function CustomAccessDetailModal({
  open,
  onClose,
  onEdit,
  category,
  license,
  appHostname,
  token,
}) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sort, setSort] = useState({ key: null, order: null });

  useEffect(() => {
    if (!open) return undefined;
    const onKeyDown = (event) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [open, onClose]);

  useEffect(() => {
    if (!open || !license?.id || !category?.id) {
      setItems([]);
      setSort({ key: null, order: null });
      return undefined;
    }

    let cancelled = false;

    async function loadItems() {
      setLoading(true);
      setSort({ key: null, order: null });
      try {
        const data = await fetchLicenseDetail({
          appHostname,
          token,
          licenseId: license.id,
        });
        if (cancelled) return;
        if (data.status !== 'OK') {
          message.error(data.message || 'โหลดรายการข้อยกเว้นไม่สำเร็จ');
          setItems([]);
          return;
        }

        const categoryName = categoryDisplayName(category);
        const nextItems = (data.product_access_items || [])
          .filter((item) => item.productcategory_id === category.id)
          .map((item) => ({
            id: item.productitem_id,
            name: item.productitem_name || '',
            inter: item.productitem_inter || '',
            categoryName,
            access: item.access_type || 'none',
          }));
        setItems(nextItems);
      } catch (error) {
        if (!cancelled) {
          message.error(error.message || 'เชื่อมต่อเซิร์ฟเวอร์ล้มเหลว');
          setItems([]);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    loadItems();
    return () => {
      cancelled = true;
    };
  }, [open, license?.id, category?.id, category, appHostname, token]);

  const handleSort = useCallback((key) => {
    setSort((prev) => {
      if (prev.key !== key) {
        return { key, order: 'asc' };
      }
      if (prev.order === 'asc') {
        return { key, order: 'desc' };
      }
      return { key: null, order: null };
    });
  }, []);

  const sortedItems = useMemo(() => {
    if (!sort.key || !sort.order) return items;
    const next = [...items];
    next.sort((left, right) => {
      const result = compareThaiText(left[sort.key], right[sort.key]);
      return sort.order === 'asc' ? result : -result;
    });
    return next;
  }, [items, sort.key, sort.order]);

  if (!open || !category || !license) return null;

  const licenseName = license.name || '';
  const categoryName = categoryDisplayName(category);

  return createPortal(
    <div className='category-license-detail-overlay' onClick={onClose} role='presentation'>
      <div
        className='category-license-detail-modal'
        role='dialog'
        aria-modal='true'
        aria-labelledby='category-license-detail-title'
        onClick={(event) => event.stopPropagation()}
      >
        <div className='category-license-detail-header'>
          <h2 id='category-license-detail-title' className='category-license-detail-title'>
            <span>{licenseName}</span>
            <span className='category-license-detail-title-sep' aria-hidden='true'>
              -
            </span>
            <span>{categoryName}</span>
          </h2>
          <button
            type='button'
            className='category-license-detail-close'
            onClick={onClose}
            aria-label='ปิด'
          >
            <CloseOutlined />
          </button>
        </div>

        <div className='category-license-detail-body'>
          <p className='category-license-detail-subtitle'>
            <strong>ข้อยกเว้น {loading ? '…' : items.length} รายการ</strong>
          </p>
          <div className='category-license-detail-table-wrap'>
            <table className='category-license-detail-table'>
              <thead>
                <tr>
                  <th className='category-license-detail-col-title'>
                    <CategoryLicenseDetailColTitle
                      sortable
                      sortOrder={sort.key === 'name' ? sort.order : null}
                      onSort={() => handleSort('name')}
                    >
                      ชื่อสินค้า (ไทย/อังกฤษ)
                    </CategoryLicenseDetailColTitle>
                  </th>
                  <th className='category-license-detail-col-title'>
                    <CategoryLicenseDetailColTitle
                      sortable
                      sortOrder={sort.key === 'categoryName' ? sort.order : null}
                      onSort={() => handleSort('categoryName')}
                    >
                      หมวดหมู่
                    </CategoryLicenseDetailColTitle>
                  </th>
                  <th className='category-license-detail-col-title'>
                    <CategoryLicenseDetailColTitle>สิทธิ์การเข้าถึง</CategoryLicenseDetailColTitle>
                  </th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={3} className='category-license-detail-table-empty'>
                      กำลังโหลด...
                    </td>
                  </tr>
                ) : null}
                {!loading && sortedItems.length === 0 ? (
                  <tr>
                    <td colSpan={3} className='category-license-detail-table-empty'>
                      ไม่พบรายการข้อยกเว้น
                    </td>
                  </tr>
                ) : null}
                {!loading
                  ? sortedItems.map((item) => (
                      <tr key={item.id}>
                        <td>
                          <div className='category-license-detail-product-name'>
                            <span className='category-license-detail-product-th' title={item.name}>
                              {item.name}
                            </span>
                            {item.inter ? (
                              <span
                                className='category-license-detail-product-en'
                                title={item.inter}
                              >
                                {item.inter}
                              </span>
                            ) : null}
                          </div>
                        </td>
                        <td>
                          <span
                            className='category-license-detail-category'
                            title={item.categoryName}
                          >
                            {item.categoryName}
                          </span>
                        </td>
                        <td>
                          <LicenseAccessBadge type={item.access} />
                        </td>
                      </tr>
                    ))
                  : null}
              </tbody>
            </table>
          </div>
        </div>

        <div className='category-license-detail-footer'>
          <Button type='text' className='category-license-detail-btn-close' onClick={onClose}>
            ปิด
          </Button>
          <Button type='primary' className='category-license-detail-btn-edit' onClick={onEdit}>
            แก้ไข
          </Button>
        </div>
      </div>
    </div>,
    document.body
  );
}

function LicenseColumnModal({
  open,
  onClose,
  onEdit,
  license,
  categories,
  cells,
  customCounts,
  appHostname,
  token,
}) {
  const [activeTab, setActiveTab] = useState('category');
  const [productItems, setProductItems] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [productSearch, setProductSearch] = useState('');
  const [productSort, setProductSort] = useState({ key: null, order: null });

  useEffect(() => {
    if (!open) return undefined;
    const onKeyDown = (event) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [open, onClose]);

  useEffect(() => {
    if (!open) {
      setActiveTab('category');
      setProductItems([]);
      setProductSearch('');
      setProductSort({ key: null, order: null });
      return undefined;
    }
    if (!license?.id) return undefined;

    let cancelled = false;

    async function loadProducts() {
      setLoadingProducts(true);
      try {
        const data = await fetchLicenseDetail({
          appHostname,
          token,
          licenseId: license.id,
        });
        if (cancelled) return;
        if (data.status !== 'OK') {
          message.error(data.message || 'โหลดรายการสินค้าไม่สำเร็จ');
          setProductItems([]);
          return;
        }
        const categoryNameById = (categories || []).reduce((acc, category) => {
          acc[category.id] = categoryDisplayName(category);
          return acc;
        }, {});
        setProductItems(
          (data.product_access_items || []).map((item) => ({
            id: item.productitem_id,
            name: item.productitem_name || '',
            inter: item.productitem_inter || '',
            categoryName: categoryNameById[item.productcategory_id] || '',
            access: item.access_type || 'none',
          })),
        );
      } catch (error) {
        if (!cancelled) {
          message.error(error.message || 'เชื่อมต่อเซิร์ฟเวอร์ล้มเหลว');
          setProductItems([]);
        }
      } finally {
        if (!cancelled) {
          setLoadingProducts(false);
        }
      }
    }

    loadProducts();
    return () => {
      cancelled = true;
    };
  }, [open, license?.id, license, categories, appHostname, token]);

  const handleProductSort = useCallback((key) => {
    setProductSort((prev) => {
      if (prev.key !== key) {
        return { key, order: 'asc' };
      }
      if (prev.order === 'asc') {
        return { key, order: 'desc' };
      }
      return { key: null, order: null };
    });
  }, []);

  const filteredProductItems = useMemo(() => {
    const keyword = productSearch.trim().toLowerCase();
    let next = productItems;
    if (keyword) {
      next = productItems.filter((item) => {
        const name = String(item.name || '').toLowerCase();
        const inter = String(item.inter || '').toLowerCase();
        const code = String(item.id || '').toLowerCase();
        return name.includes(keyword) || inter.includes(keyword) || code.includes(keyword);
      });
    }
    if (!productSort.key || !productSort.order) {
      return next;
    }
    const sorted = [...next];
    sorted.sort((left, right) => {
      const result = compareThaiText(left[productSort.key], right[productSort.key]);
      return productSort.order === 'asc' ? result : -result;
    });
    return sorted;
  }, [productItems, productSearch, productSort.key, productSort.order]);

  if (!open || !license) return null;

  const licenseId = license.id;
  const categoryRows = (categories || []).map((category) => ({
    id: category.id,
    name: categoryDisplayName(category),
    access: cells?.[category.id]?.[licenseId] || 'none',
    customCount: customCounts?.[category.id]?.[licenseId] || 0,
  }));
  const productCount = productItems.length;

  return createPortal(
    <div className='category-license-detail-overlay' onClick={onClose} role='presentation'>
      <div
        className='category-license-detail-modal'
        role='dialog'
        aria-modal='true'
        aria-labelledby='category-license-column-title'
        onClick={(event) => event.stopPropagation()}
      >
        <div className='category-license-detail-header'>
          <h2 id='category-license-column-title' className='category-license-detail-title'>
            <span>{license.name || ''}</span>
          </h2>
          <button
            type='button'
            className='category-license-detail-close'
            onClick={onClose}
            aria-label='ปิด'
          >
            <CloseOutlined />
          </button>
        </div>

        <div className='category-license-column-tabs' role='tablist'>
          <button
            type='button'
            role='tab'
            aria-selected={activeTab === 'category'}
            className={`category-license-column-tab${
              activeTab === 'category' ? ' category-license-column-tab--active' : ''
            }`}
            onClick={() => setActiveTab('category')}
          >
            สิทธิ์ตามหมวดหมู่สินค้า
          </button>
          <button
            type='button'
            role='tab'
            aria-selected={activeTab === 'product'}
            className={`category-license-column-tab${
              activeTab === 'product' ? ' category-license-column-tab--active' : ''
            }`}
            onClick={() => setActiveTab('product')}
          >
            สิทธิ์ตามรายการสินค้า ({loadingProducts ? '…' : productCount})
          </button>
        </div>

        <div className='category-license-detail-body category-license-column-body'>
          {activeTab === 'category' ? (
            <div className='category-license-detail-table-wrap'>
              <table className='category-license-detail-table category-license-column-table category-license-column-table--categories'>
                <thead>
                  <tr>
                    <th>ชื่อหมวดหมู่</th>
                    <th>สิทธิ์การเข้าถึง</th>
                  </tr>
                </thead>
                <tbody>
                  {categoryRows.length === 0 ? (
                    <tr>
                      <td colSpan={2} className='category-license-detail-table-empty'>
                        ไม่พบหมวดหมู่
                      </td>
                    </tr>
                  ) : (
                    categoryRows.map((row) => (
                      <tr key={row.id}>
                        <td>
                          <span className='category-license-detail-category' title={row.name}>
                            {row.name}
                          </span>
                        </td>
                        <td>
                          <LicenseAccessBadge
                            type={row.access}
                            customCount={row.customCount}
                            onClick={
                              row.customCount > 0
                                ? () => setActiveTab('product')
                                : undefined
                            }
                          />
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          ) : (
            <div className='category-license-column-product-panel'>
              <Input
                value={productSearch}
                onChange={(event) => setProductSearch(event.target.value)}
                placeholder='ค้นหาชื่อสินค้า หรือรหัสสินค้า...'
                prefix={<SearchOutlined className='category-license-column-search-icon' />}
                className='category-license-column-search'
                allowClear
              />
              <div className='category-license-detail-table-wrap'>
                <table className='category-license-detail-table category-license-column-table category-license-column-table--products'>
                  <thead>
                    <tr>
                      <th className='category-license-detail-col-title'>
                        <CategoryLicenseDetailColTitle
                          sortable
                          sortOrder={productSort.key === 'name' ? productSort.order : null}
                          onSort={() => handleProductSort('name')}
                        >
                          ชื่อสินค้า (ไทย/อังกฤษ)
                        </CategoryLicenseDetailColTitle>
                      </th>
                      <th className='category-license-detail-col-title'>
                        <CategoryLicenseDetailColTitle
                          sortable
                          sortOrder={
                            productSort.key === 'categoryName' ? productSort.order : null
                          }
                          onSort={() => handleProductSort('categoryName')}
                        >
                          หมวดหมู่
                        </CategoryLicenseDetailColTitle>
                      </th>
                      <th className='category-license-detail-col-title'>
                        <CategoryLicenseDetailColTitle>สิทธิ์การเข้าถึง</CategoryLicenseDetailColTitle>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {loadingProducts ? (
                      <tr>
                        <td colSpan={3} className='category-license-detail-table-empty'>
                          กำลังโหลด...
                        </td>
                      </tr>
                    ) : null}
                    {!loadingProducts && filteredProductItems.length === 0 ? (
                      <tr>
                        <td colSpan={3} className='category-license-detail-table-empty'>
                          ไม่พบรายการสินค้า
                        </td>
                      </tr>
                    ) : null}
                    {!loadingProducts
                      ? filteredProductItems.map((item) => (
                          <tr key={item.id}>
                            <td>
                              <div className='category-license-detail-product-name'>
                                <span
                                  className='category-license-detail-product-th'
                                  title={item.name}
                                >
                                  {item.name}
                                </span>
                                {item.inter ? (
                                  <span
                                    className='category-license-detail-product-en'
                                    title={item.inter}
                                  >
                                    {item.inter}
                                  </span>
                                ) : null}
                              </div>
                            </td>
                            <td>
                              <span
                                className='category-license-detail-category'
                                title={item.categoryName}
                              >
                                {item.categoryName}
                              </span>
                            </td>
                            <td>
                              <LicenseAccessBadge type={item.access} />
                            </td>
                          </tr>
                        ))
                      : null}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>

        <div className='category-license-detail-footer'>
          <Button type='text' className='category-license-detail-btn-close' onClick={onClose}>
            ปิด
          </Button>
          <Button type='primary' className='category-license-detail-btn-edit' onClick={onEdit}>
            แก้ไข
          </Button>
        </div>
      </div>
    </div>,
    document.body
  );
}

export function CategoryNameCell({ category }) {
  if (category.multiline) {
    return (
      <>
        {category.multiline.map((line, index) => (
          <React.Fragment key={line}>
            {index > 0 && <br />}
            {line}
          </React.Fragment>
        ))}
      </>
    );
  }

  return category.name;
}

function ProductCategoryLicense({ app_hostname, permitdata }) {
  const navigate = useNavigate();
  const location = useLocation();
  const refreshKey = location.state?.refresh;

  const [matrix, setMatrix] = useState(EMPTY_MATRIX);
  const [loading, setLoading] = useState(true);
  const [detailContext, setDetailContext] = useState(null);
  const [licenseColumnModal, setLicenseColumnModal] = useState(null);

  const openCustomDetail = useCallback((category, license) => {
    setDetailContext({ category, license });
  }, []);

  const closeCustomDetail = useCallback(() => {
    setDetailContext(null);
  }, []);

  const openLicenseColumnModal = useCallback((license) => {
    setLicenseColumnModal(license);
  }, []);

  const closeLicenseColumnModal = useCallback(() => {
    setLicenseColumnModal(null);
  }, []);

  const editCustomDetail = useCallback(() => {
    const licenseId = detailContext?.license?.id;
    setDetailContext(null);
    navigate('/product/category/license/edit', {
      state: licenseId ? { licenseId } : undefined,
    });
  }, [detailContext?.license?.id, navigate]);

  const editLicenseColumn = useCallback(() => {
    const licenseId = licenseColumnModal?.id;
    setLicenseColumnModal(null);
    navigate('/product/category/license/edit', {
      state: licenseId ? { licenseId } : undefined,
    });
  }, [licenseColumnModal?.id, navigate]);

  const loadMatrix = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchLicenseMatrix({
        appHostname: app_hostname,
        token: permitdata?.token,
      });
      if (data.status !== 'OK') {
        message.error(data.message || 'โหลด matrix ไม่สำเร็จ');
        return;
      }
      setMatrix(toMatrixSnapshot(data));
    } catch (error) {
      message.error(error.message || 'เชื่อมต่อเซิร์ฟเวอร์ล้มเหลว');
    } finally {
      setLoading(false);
    }
  }, [app_hostname, permitdata?.token]);

  useEffect(() => {
    loadMatrix();
  }, [loadMatrix, refreshKey]);

  const snapshot = matrix || EMPTY_MATRIX;
  const licenseColumns = snapshot.columns || [];
  const categoryRows = snapshot.rows || [];
  const cells = snapshot.cells || {};
  const customCounts = snapshot.customCounts || {};
  const showEmptyLicenses = !loading && licenseColumns.length === 0;

  return (
    <div className='page-wrapper category-license-page-wrapper'>
      <div className='page-card category-license-page'>
        <div className='category-license-body'>
          <div className='page-header-actions category-license-header'>
            <div className='category-license-title-block'>
              <Title level={3} className='category-license-title'>
                <SafetyCertificateOutlined className='category-license-title-icon' />
                สิทธิ์การเข้าถึงหมวดหมู่สินค้า
              </Title>
              <Text type='secondary' className='category-license-subtitle'>
                จัดการสิทธิ์การนำเข้า จัดซื้อ และจัดจำหน่ายเวชภัณฑ์ตามกฎหมายควบคุมยาแห่งประเทศไทย
              </Text>
            </div>
            <Space size={8} wrap className='category-license-actions'>
              <Button
                icon={<EditOutlined />}
                className='category-license-btn-edit'
                onClick={() => navigate('/product/category/license/edit')}
              >
                แก้ไข
              </Button>
              <Button
                type='primary'
                icon={<PlusOutlined />}
                className='category-license-btn-add'
                onClick={() => navigate('/product/category/license/new')}
              >
                เพิ่มใหม่
              </Button>
            </Space>
          </div>

          <div className='category-license-content'>
            {loading ? (
              <Text type='secondary'>กำลังโหลด...</Text>
            ) : null}
            {showEmptyLicenses ? (
              <Text type='secondary' className='category-license-empty'>
                ยังไม่มีใบทะเบียน — กด &quot;เพิ่มใหม่&quot; เพื่อสร้างใบแรก
              </Text>
            ) : null}
            <div className='category-license-legend'>
              <span className='category-license-legend-label'>คำอธิบาย:</span>
              <LicenseAccessBadge type='buy-sell' />
              <LicenseAccessBadge type='buy-only' />
              <LicenseAccessBadge type='sell-only' />
              <LicenseAccessBadge type='none' />
              <LicenseAccessBadge type='custom' />
            </div>

            <div className='category-license-table-scroll'>
              <table className='category-license-table'>
                <thead>
                  <tr>
                    <th className='category-license-table-cell category-license-table-cell--corner'>
                      หมวดหมู่ / ใบทะเบียน
                    </th>
                    {licenseColumns.map((license) => (
                      <th
                        key={license.id}
                        className='category-license-table-cell category-license-table-cell--header category-license-table-cell--license-header'
                      >
                        <button
                          type='button'
                          className='category-license-table-license-btn'
                          onClick={() => openLicenseColumnModal(license)}
                        >
                          {license.name}
                        </button>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {categoryRows.map((category, rowIndex) => (
                    <tr
                      key={category.id}
                      className={
                        rowIndex % 2 === 1 ? 'category-license-table-row--striped' : undefined
                      }
                    >
                      <th
                        scope='row'
                        className='category-license-table-cell category-license-table-cell--category'
                      >
                        <CategoryNameCell category={category} />
                      </th>
                      {licenseColumns.map((license) => {
                        const customCount = customCounts[category.id]?.[license.id] || 0;
                        return (
                          <td key={license.id} className='category-license-table-cell'>
                            <LicenseAccessBadge
                              type={cells[category.id]?.[license.id] || 'none'}
                              customCount={customCount}
                              onClick={
                                customCount > 0
                                  ? () => openCustomDetail(category, license)
                                  : undefined
                              }
                            />
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      <CustomAccessDetailModal
        open={Boolean(detailContext)}
        onClose={closeCustomDetail}
        onEdit={editCustomDetail}
        category={detailContext?.category}
        license={detailContext?.license}
        appHostname={app_hostname}
        token={permitdata?.token}
      />
      <LicenseColumnModal
        open={Boolean(licenseColumnModal)}
        onClose={closeLicenseColumnModal}
        onEdit={editLicenseColumn}
        license={licenseColumnModal}
        categories={categoryRows}
        cells={cells}
        customCounts={customCounts}
        appHostname={app_hostname}
        token={permitdata?.token}
      />
    </div>
  );
}

export default ProductCategoryLicense;
